import { CacheEntry } from '../types';

const CACHE_KEYS = {
  LOCATION: 'weather_location_cache',
  WEATHER: 'weather_data_cache',
} as const;

export class CacheManager {
  private static readonly DEFAULT_EXPIRY_MINUTES = 30;
  private static readonly SOFT_EXPIRY_MINUTES = 25; // Refresh before hard expiry

  static set<T>(key: string, data: T, expiryMinutes: number = this.DEFAULT_EXPIRY_MINUTES): void {
    try {
      const now = new Date();
      const expiresAt = new Date(now.getTime() + expiryMinutes * 60 * 1000);
      
      const cacheEntry: CacheEntry<T> = {
        data,
        timestamp: now,
        expiresAt,
      };

      localStorage.setItem(key, JSON.stringify(cacheEntry));
      
      // Store cache metadata for performance monitoring
      this.setCacheMetadata(key, {
        size: JSON.stringify(cacheEntry).length,
        expiryMinutes,
        timestamp: now
      });
    } catch (error) {
      console.warn('Failed to cache data:', error);
      // Try to clear some space if storage is full
      if (error instanceof DOMException && error.code === 22) {
        this.clearOldestEntries();
        // Retry once
        try {
          localStorage.setItem(key, JSON.stringify({ data, timestamp: new Date(), expiresAt: new Date(Date.now() + expiryMinutes * 60 * 1000) }));
        } catch (retryError) {
          console.error('Failed to cache data after cleanup:', retryError);
        }
      }
    }
  }

  static get<T>(key: string): T | null {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const cacheEntry: CacheEntry<T> = JSON.parse(cached);
      const now = new Date();
      const expiresAt = new Date(cacheEntry.expiresAt);

      if (now > expiresAt) {
        localStorage.removeItem(key);
        return null;
      }

      return cacheEntry.data;
    } catch (error) {
      console.warn('Failed to retrieve cached data:', error);
      return null;
    }
  }

  static clear(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Failed to clear cache:', error);
    }
  }

  static clearAll(): void {
    Object.values(CACHE_KEYS).forEach(key => this.clear(key));
    this.clearCacheMetadata();
  }

  static isSoftExpired(key: string): boolean {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return true;

      const cacheEntry: CacheEntry<unknown> = JSON.parse(cached);
      const now = new Date();
      const softExpiresAt = new Date(cacheEntry.timestamp.getTime() + this.SOFT_EXPIRY_MINUTES * 60 * 1000);

      return now > softExpiresAt;
    } catch {
      return true;
    }
  }

  static getWithMetadata<T>(key: string): { data: T; isExpired: boolean; isSoftExpired: boolean } | null {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const cacheEntry: CacheEntry<T> = JSON.parse(cached);
      const now = new Date();
      const expiresAt = new Date(cacheEntry.expiresAt);
      const softExpiresAt = new Date(cacheEntry.timestamp.getTime() + this.SOFT_EXPIRY_MINUTES * 60 * 1000);

      const isExpired = now > expiresAt;
      const isSoftExpired = now > softExpiresAt;

      if (isExpired) {
        localStorage.removeItem(key);
        return null;
      }

      return {
        data: cacheEntry.data,
        isExpired,
        isSoftExpired
      };
    } catch (error) {
      console.warn('Failed to retrieve cached data with metadata:', error);
      return null;
    }
  }

  private static setCacheMetadata(key: string, metadata: { size: number; expiryMinutes: number; timestamp: Date }): void {
    try {
      const metadataKey = `${key}_metadata`;
      localStorage.setItem(metadataKey, JSON.stringify(metadata));
    } catch (error) {
      // Metadata is not critical, just log the error
      console.warn('Failed to store cache metadata:', error);
    }
  }

  private static clearCacheMetadata(): void {
    try {
      Object.values(CACHE_KEYS).forEach(key => {
        const metadataKey = `${key}_metadata`;
        localStorage.removeItem(metadataKey);
      });
    } catch (error) {
      console.warn('Failed to clear cache metadata:', error);
    }
  }

  private static clearOldestEntries(): void {
    try {
      const entries: Array<{ key: string; timestamp: Date }> = [];
      
      Object.values(CACHE_KEYS).forEach(key => {
        const cached = localStorage.getItem(key);
        if (cached) {
          try {
            const cacheEntry = JSON.parse(cached);
            entries.push({ key, timestamp: new Date(cacheEntry.timestamp) });
          } catch {
            // Remove corrupted entries
            localStorage.removeItem(key);
          }
        }
      });

      // Sort by timestamp and remove oldest entries
      entries.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      const entriesToRemove = Math.ceil(entries.length / 2); // Remove half of the entries
      
      for (let i = 0; i < entriesToRemove && i < entries.length; i++) {
        this.clear(entries[i].key);
      }
    } catch (error) {
      console.warn('Failed to clear oldest cache entries:', error);
    }
  }

  static getCacheStats(): { totalSize: number; entryCount: number; oldestEntry?: Date } {
    let totalSize = 0;
    let entryCount = 0;
    let oldestEntry: Date | undefined;

    Object.values(CACHE_KEYS).forEach(key => {
      const cached = localStorage.getItem(key);
      if (cached) {
        totalSize += cached.length;
        entryCount++;
        
        try {
          const cacheEntry = JSON.parse(cached);
          const timestamp = new Date(cacheEntry.timestamp);
          if (!oldestEntry || timestamp < oldestEntry) {
            oldestEntry = timestamp;
          }
        } catch {
          // Ignore parsing errors for stats
        }
      }
    });

    return { totalSize, entryCount, oldestEntry };
  }

  static isExpired(key: string): boolean {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return true;

      const cacheEntry: CacheEntry<unknown> = JSON.parse(cached);
      const now = new Date();
      const expiresAt = new Date(cacheEntry.expiresAt);

      return now > expiresAt;
    } catch {
      return true;
    }
  }
}

export const WEATHER_CACHE_KEYS = CACHE_KEYS;